/**
 * GUID Cell Renderer
 *
 * Renders the vulnerability GUID (first block only) with tooltip for full UUID.
 * Clicking the copy icon copies the full UUID to clipboard.
 */

import React from 'react';

import { CopyToClipboard } from '@/components/CopyToClipboard';
import { Tooltip } from '@/components/Tooltip';
import type { RiskWithVulnerability } from '@/hooks/useRisks';

interface GuidCellProps {
  risk: RiskWithVulnerability;
}

/**
 * Memoized cell renderer for GUID column
 *
 * Displays truncated first UUID block with full UUID on hover.
 * The copy icon copies the full UUID to clipboard.
 *
 * @param risk - The vulnerability/risk
 * @returns Formatted GUID or dash if unavailable
 */
export const GuidCell: React.FC<GuidCellProps> = React.memo(({ risk }) => {
  if (!risk.guid) {
    return <>-</>;
  }

  const firstBlock = risk.guid.substring(
    0,
    risk.guid.indexOf('-') > 0 ? risk.guid.indexOf('-') : 8
  );

  return (
    <CopyToClipboard textToCopy={risk.guid} className="gap-1">
      <Tooltip title={risk.guid}>
        <span className="text-chariot-text-secondary font-mono text-xs">
          {firstBlock}
        </span>
      </Tooltip>
    </CopyToClipboard>
  );
});

GuidCell.displayName = 'GuidCell';
